import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  Inject,
  OnInit,
  OnDestroy,
} from '@angular/core';
import {
  BehaviorSubject,
  catchError,
  EMPTY,
  filter,
  firstValueFrom,
  forkJoin,
  map,
  of,
  share,
  Subject,
  switchMap,
  tap,
  withLatestFrom,
} from 'rxjs';
import {
  POLYMORPHEUS_CONTEXT,
  PolymorpheusComponent,
} from '@tinkoff/ng-polymorpheus';
import {
  TuiButtonModule,
  TuiDialogContext,
  TuiDialogService,
  TuiLoaderModule,
  TuiSvgModule,
  TuiTextfieldControllerModule,
} from '@taiga-ui/core';
import { AttachmentService } from '@shared/services/attachment.service';
import { FormBuilder, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { AlertService } from '@shared/services/alert.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { AsyncPipe } from '@angular/common';
import { FilesInputComponent } from '@shared/components/forms/files-input/files-input.component';
import { mimeTypes } from '@shared/constants/mime-types';
import { roundDecimal } from '@shared/utils/math';
import {
  TuiInputModule,
  TuiInputNumberModule,
  TuiToggleModule,
} from '@taiga-ui/kit';
import { CURRENCIES } from '@shared/constants/currency';
import { AutocompleteComponent } from '@shared/components/forms/autocomplete/autocomplete.component';
import { CargoCatalogsService } from '@shared/services/cargo-catalogs.service';
import { FormsService } from '@shared/services/forms.service';
import { InputDateComponent } from '@shared/components/forms/input-date/input-date.component';
import { InvoiceProcessingService } from '@shared/services/invoice-processing.service';
import { InvoiceService } from '@shared/services/invoice.service';
import { InvoiceParseDialogComponent } from '@shared/components/invoice/invoice-parse-dialog/invoice-parse-dialog.component';
import { TuiLetModule } from '@taiga-ui/cdk';
import { openFileInNewTab } from '@shared/utils/download-file';
import { WINDOW } from '@shared/providers/window.provider';
import { WebsocketService } from '@shared/services/websocket.service';
import { Attachment } from '../../../../../../types/attachment';
import {
  CargoOrder,
  CargoOrderForm,
  SellerJsonForm,
} from '../../../../../../types/cargo';
import {
  InvoiceProcess,
  InvoiceProcessStatus,
} from '../../../../../../types/invoice-process';
import { InvoiceDto } from '../../../../../../types/invoice';
import { CargoLoadingService } from '../../../../services/cargo-loading.service';
import { WebsocketEvent } from '../../../../../../types/websocket';
import { CommonStateService } from '../../../../services/common-state.service';

export type OrderAttachmentsInputData = {
  cargoId: number;
  order: CargoOrder;
  canUpload: boolean;
  canRemove: boolean;
  canEdit: boolean;
};

type OrderAttachmentsForm = Pick<
  CargoOrderForm,
  | 'buyerCode'
  | 'airWaybillNumber'
  | 'imported'
  | 'invoiceNumber'
  | 'invoiceDate'
  | 'prepayment'
  | 'invoiceCurrencyLetter'
  | 'invoiceCost'
> & {
  brokerJson: FormGroup<SellerJsonForm>;
};

type AttachmentWithProcessDetails = Attachment & {
  process?: InvoiceProcess;
};
@Component({
  selector: 'app-pre-order-attachments',
  standalone: true,
  imports: [
    AsyncPipe,
    FilesInputComponent,
    TuiButtonModule,
    ReactiveFormsModule,
    TuiInputModule,
    TuiInputNumberModule,
    TuiTextfieldControllerModule,
    AutocompleteComponent,
    TuiToggleModule,
    InputDateComponent,
    TuiSvgModule,
    TuiLoaderModule,
    TuiLetModule,
  ],
  templateUrl: './order-attachments.component.html',
  styleUrl: './order-attachments.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class OrderAttachmentsComponent implements OnInit, OnDestroy {
  refresh$ = new Subject<void>();
  attachmentId$ = new BehaviorSubject<Nullable<number>>(null);
  cargoId$ = new BehaviorSubject<number>(0);
  attachmentsWithProcessDetails$ = new BehaviorSubject<
    AttachmentWithProcessDetails[]
  >([]);

  openFileInNewTab$ = new Subject<string>();

  uploading$ = new BehaviorSubject(false);
  loadingDetails$ = new BehaviorSubject<Set<number>>(new Set());
  attach$ = new Subject<void>();
  saveChanges$ = new Subject<{ close: boolean }>();
  download$ = new Subject<void>();
  delete$ = new Subject<void>();
  files = this.fb.control<Blob[]>([]);
  protected readonly mimeTypes = mimeTypes;
  protected readonly roundDecimal = roundDecimal;
  orderForm = this.fb.group<OrderAttachmentsForm>({
    invoiceCost: this.fb.control({ value: null, disabled: false }),
    invoiceCurrencyLetter: this.fb.control({ value: null, disabled: false }),
    invoiceDate: this.fb.control({ value: null, disabled: false }),
    invoiceNumber: this.fb.control({ value: null, disabled: false }),
    prepayment: this.fb.control({ value: null, disabled: true }),
    buyerCode: this.fb.control({ value: null, disabled: true }),
    airWaybillNumber: this.fb.control({ value: null, disabled: true }),
    imported: this.fb.control({ value: null, disabled: true }),
    brokerJson: this.forms.getSellerJsonForm(),
  });

  protected readonly CURRENCIES = CURRENCIES;
  sellers$ = this.catalogs.sellers$;
  protected readonly InvoiceProcessStatus = InvoiceProcessStatus;
  openInvoice$ = new Subject<InvoiceProcess['invoiceId']>();
  constructor(
    @Inject(POLYMORPHEUS_CONTEXT)
    protected readonly context: TuiDialogContext<
      CargoOrder,
      OrderAttachmentsInputData
    >,
    @Inject(WINDOW) private readonly window: Window,
    private readonly attachmentService: AttachmentService,
    private readonly destroyRef: DestroyRef,
    private readonly fb: FormBuilder,
    private readonly alert: AlertService,
    protected readonly catalogs: CargoCatalogsService,
    private readonly forms: FormsService,
    private readonly invoiceProcessingService: InvoiceProcessingService,
    private readonly invoiceService: InvoiceService,
    private readonly dialogs: TuiDialogService,
    private readonly cargoLoadingService: CargoLoadingService,
    private readonly ws: WebsocketService,
    private readonly commonStateService: CommonStateService,
  ) {}

  onDownload() {
    this.download$
      .pipe(
        map(() => this.attachmentId$.value),
        filter((attachmentId): attachmentId is number => !!attachmentId),
        switchMap(attachmentId =>
          this.attachmentService
            .download(this.cargoId$.value, attachmentId)
            .pipe(
              catchError(() => {
                this.alert.showError();
                return of(null);
              }),
            ),
        ),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();
  }

  onRefresh() {
    this.refresh$
      .pipe(
        switchMap(() => {
          if (this.attachmentId$.value) {
            return forkJoin({
              attachments: this.attachmentService
                .getFileListFromAttachment(
                  this.cargoId$.value,
                  this.attachmentId$.value,
                )
                .pipe(
                  catchError(() => {
                    this.alert.showError('Не удалось загрузить файлы');
                    return of([]);
                  }),
                ),
              processes: this.invoiceProcessingService
                .getByOrderId(this.orderId)
                .pipe(
                  catchError(() => {
                    this.alert.showError(
                      'Не удалось загрузить статусы обработки',
                    );
                    return of([]);
                  }),
                ),
            });
          }
          return of({ attachments: [], processes: [] });
        }),
        tap(({ attachments, processes }) => {
          const withProcessDetails = attachments.map(attachment => {
            const result: AttachmentWithProcessDetails = attachment;
            const process = processes.find(p => p.fileName === attachment.name);
            if (process) {
              result.process = process;
            }
            return result;
          });
          this.attachmentsWithProcessDetails$.next(withProcessDetails);
        }),
      )
      .subscribe();
  }

  onDelete() {
    this.delete$
      .pipe(
        map(() => this.attachmentId$.value),
        filter((attachmentId): attachmentId is number => !!attachmentId),
        switchMap(attachmentId =>
          this.attachmentService.delete(this.cargoId$.value, attachmentId).pipe(
            tap(() => {
              this.attachmentId$.next(null);
              this.attachmentsWithProcessDetails$.next([]);
              this.saveOrder();
            }),
            catchError(() => {
              this.alert.showError();
              return of(null);
            }),
          ),
        ),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();
  }

  onSaveChanges() {
    this.saveChanges$
      .pipe(
        tap(() => this.uploading$.next(true)),
        switchMap(config => this.attachFiles().pipe(map(() => config))),
        tap(config => {
          this.saveOrder();
          this.uploading$.next(false);
          if (config.close) {
            this.context.$implicit.complete();
          }
        }),
        catchError(() => {
          this.alert.showError();
          this.uploading$.next(false);
          return of(null);
        }),
      )
      .subscribe();
  }

  onHandleInvoice() {
    this.handleInvoice$
      .pipe(
        withLatestFrom(this.cargoId$, this.attachmentId$),
        switchMap(([attachment, cargoId, attachmentId]) =>
          this.invoiceService
            .handle({
              cargoId,
              attachmentId: attachmentId ?? 0,
              orderId: this.orderId,
              fileName: attachment.name,
            })
            .pipe(
              tap(() => {
                this.commonStateService.refreshActiveProcesses$.next();
                this.alert.showInfo('Файл добавлен в очередь');
                this.refresh$.next();
              }),
              catchError(err => {
                this.alert.showError(err.error);
                return EMPTY;
              }),
            ),
        ),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();
  }

  onOpenInvoice() {
    this.openInvoice$
      .pipe(
        filter((id): id is number => id !== null),
        tap(id => this.setLoading(id)),
        switchMap(id =>
          this.invoiceService.getById(id).pipe(
            tap(() => {
              this.removeLoading(id);
            }),
            catchError(() => {
              this.removeLoading(id);
              return EMPTY;
            }),
          ),
        ),
        switchMap(invoice =>
          this.dialogs
            .open<InvoiceDto | void>(
              new PolymorpheusComponent(InvoiceParseDialogComponent),
              {
                size: 'page',
                closeable: false,
                data: {
                  invoice,
                  confirmText: 'Применить',
                },
              },
            )
            .pipe(
              filter(
                (updatedInvoice): updatedInvoice is InvoiceDto =>
                  !!updatedInvoice,
              ),
              tap(updatedInvoice => {
                this.fillInvoiceDetails(updatedInvoice);
              }),
            ),
        ),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();
  }

  onOpenFileInNewTab() {
    return this.openFileInNewTab$
      .pipe(
        withLatestFrom(this.attachmentId$, this.cargoId$),
        switchMap(([fileName, id, cargoId]) =>
          this.attachmentService
            .getSingleFile({
              id: id ?? 0,
              cargoId,
              fileName,
            })
            .pipe(
              tap(res => {
                if (res.body) {
                  openFileInNewTab(res, this.window);
                }
              }),
            ),
        ),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();
  }

  handleInvoice$ = new Subject<AttachmentWithProcessDetails>();
  fillInvoiceDetails(invoice: InvoiceDto) {
    this.orderForm.patchValue({
      invoiceCost: invoice.cost,
      invoiceDate: invoice.date,
      invoiceNumber: invoice.number,
      invoiceCurrencyLetter: invoice.currencyLetter,
    });
  }

  setLoading(id: number) {
    this.loadingDetails$.next(this.loadingDetails$.value.add(id));
  }

  removeLoading(id: number) {
    const value = this.loadingDetails$.value;
    value.delete(id);
    this.loadingDetails$.next(value);
  }

  get orderId() {
    return this.context.data.order.id ?? 0;
  }

  get isImported() {
    return this.orderForm.controls.imported.getRawValue();
  }

  get canEdit() {
    return this.context.data.canEdit;
  }

  get canRemove() {
    return this.context.data.canRemove;
  }

  get canUpload() {
    return this.context.data.canUpload;
  }

  getFinalOrder() {
    return {
      ...this.context.data.order,
      ...this.orderForm.getRawValue(),
      attachmentId: this.attachmentId$.value,
    };
  }

  saveOrder() {
    const order: CargoOrder = this.getFinalOrder();
    this.context.$implicit.next(order);
  }

  attachFiles() {
    const files = this.files.getRawValue() ?? [];
    if (!files.length) {
      return of(null);
    }
    const formData = new FormData();
    files.forEach((file, index) => {
      const fileKey = `file${index + 1}`;
      formData.append(fileKey, file as unknown as Blob);
    });

    return this.attachmentService
      .attach(this.cargoId$.value, this.attachmentId$.value, formData)
      .pipe(
        tap(id => {
          this.files.reset();
          this.attachmentId$.next(id);
          this.refresh$.next();
        }),
        catchError(() => {
          this.alert.showError();
          return of(null);
        }),
      );
  }

  setInitialData() {
    const data = this.context.data;
    this.attachmentId$.next(data.order.attachmentId);
    this.orderForm.patchValue(data.order);
    this.orderForm.controls.brokerJson.disable();
  }

  saveChangesWhenDialogClose() {
    if (!this.uploading$.getValue()) {
      const stream = this.attachFiles().pipe(
        switchMap(id => {
          if (id !== null || this.orderForm.dirty) {
            return this.cargoLoadingService
              .updateOrder(this.getFinalOrder())
              .pipe(
                tap(() => {
                  this.alert.showSuccess();
                }),
              );
          }
          return of(null);
        }),
        catchError(err => {
          this.alert.showError(err.error);
          return EMPTY;
        }),
        share(),
      );
      firstValueFrom(stream);
    }
  }

  ngOnDestroy() {
    this.saveChangesWhenDialogClose();
  }

  syncDataOnProcessingStatusChange() {
    return this.ws.cargoEvents$
      .pipe(
        filter(
          wsEvent =>
            wsEvent.event === WebsocketEvent.INVOICE_PROCESSING_CHANGE_STATUS,
        ),
        tap(() => this.refresh$.next()),
        switchMap(() => this.syncFormStateWithUpdatedOrder()),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();
  }

  syncFormStateWithUpdatedOrder() {
    return this.cargoLoadingService
      .getOrdersByCargoId(this.cargoId$.getValue())
      .pipe(
        tap(orders => {
          const order = orders.find(order => order.id === this.orderId);
          if (order) {
            this.orderForm.patchValue(order);
          }
        }),
        catchError(err => {
          this.alert.showError(err.error);
          return EMPTY;
        }),
      );
  }

  ngOnInit() {
    this.cargoId$.next(this.context.data.cargoId);
    this.onOpenFileInNewTab();
    this.setInitialData();
    this.onSaveChanges();
    this.onRefresh();
    this.onDownload();
    this.onDelete();
    this.onHandleInvoice();
    this.onOpenInvoice();
    this.syncDataOnProcessingStatusChange();
    this.refresh$.next();

    if (!this.canEdit) {
      this.files.disable();
      this.orderForm.disable();
    }
  }
}
